import React from 'react'
import PropTypes from 'prop-types'
import Konva from 'konva'
import { Image } from 'react-konva';
import _ from 'lodash'
import { Color, ImagePosition } from '../reducers';

interface PartImageProps {
    image: HTMLCanvasElement | HTMLImageElement,
    x: number
    y: number
    z: number
    offsetX: number
    offsetY: number
    scale: number
    color: Color
    imageIndex: number,
    selected: boolean
    navType: string
    allowMove: boolean
    onSelect: (index: number) => void,
    onMove: (index: number, pos: ImagePosition, scale: number) => void,
}

class PartImage extends React.PureComponent<PartImageProps> {
    img: Konva.Image

    constructor(props) {
        super(props)

        this.onKeyDown = this.onKeyDown.bind(this)
    }

    onKeyDown(evt) {
        evt.preventDefault()

        const { props, img } = this

        if (!props.selected) return

        const delta = 1
        let pos = { ...img.absolutePosition(), z: props.z }
        const offsetX = pos.x - img.x()
        const offsetY = pos.y - img.y()
        switch (evt.key.toLowerCase()) {
            case 'a':
            case 'arrowleft':
                pos.x -= delta
                break
            case 'w':
            case 'arrowup':
                pos.y -= delta
                break
            case 'd':
            case 'arrowright':
                pos.x += delta
                break
            case 's':
            case 'arrowdown':
                pos.y += delta
                break
            case '+':
            case '=':
                pos.z += delta
                break
            case '-':
            case '_':
                pos.z -= delta
                break
            case '0':
            case ')':
            case '*':
                pos.z = 0
                break
            default:
                return
        }
        Object.assign(pos, preventDraggingOutOfStage(img, pos))
        pos.x -= offsetX + props.offsetX
        pos.y -= offsetY + props.offsetY
        props.onMove(props.imageIndex, pos, props.scale)
    }

    render() {
        const { props } = this

        const filters = []
        let brighten = undefined
        let color = undefined
        if(props.selected){
            brighten = Number(props.color.selectionBrighten)
            color = props.color.selection_color
            if(brighten !== 0) filters.push(Konva.Filters.Brighten)
            if(color.length === 6) filters.push(Konva.Filters.RGB)
        }

        return <Image
            image={props.image}
            x={props.x + props.offsetX}
            y={props.y + props.offsetY}
            scaleX={props.scale}
            scaleY={props.scale}
            filters={filters}
            red={color && parseInt(color.substring(0, 2), 16)}
            green={color && parseInt(color.substring(2, 4), 16)}
            blue={color && parseInt(color.substring(4, 6), 16)}
            brightness={brighten}
            onMouseEnter={() => {
                document.body.style.cursor = "pointer"
                if (props.navType === 'mouseenter' && !props.selected)
                    props.onSelect(props.imageIndex)
            }}
            onMouseLeave={() => {
                document.body.style.cursor = "default"
            }}
            onClick={() => {
                props.onSelect(props.imageIndex)
            }}
            onDragStart={() => {
                if (!props.selected) {
                    props.onSelect(props.imageIndex)
                }
            }}
            draggable={props.allowMove}
            dragBoundFunc={function (pos) {
                return preventDraggingOutOfStage(this, pos)
            }}
            onDragEnd={() => {
                props.onMove(
                    props.imageIndex,
                    {
                        x: this.img.x() - props.offsetX,
                        y: this.img.y() - props.offsetY,
                        z: props.z
                    },
                    props.scale,
                )
            }}
            ref={node => { this.img = node }}
        />
    }

    componentDidMount() {
        this.componentDidUpdate()
        if (this.props.allowMove) {
            const container = this.img.getStage().container()
            container.tabIndex = 1
            container.focus()
            container.addEventListener("keydown", this.onKeyDown, true)
        }
    }

    componentDidUpdate() {
        this.img.cache()
        this.img.drawHitFromCache()
    }

    componentWillUnmount() {
        if (this.props.allowMove) {
            this.img.getStage().container().removeEventListener("keydown", this.onKeyDown, true);
        }

        this.img.clearCache();
    }

    static propTypes = {
        image: PropTypes.object.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        z: PropTypes.number.isRequired,
        offsetX: PropTypes.number.isRequired,
        offsetY: PropTypes.number.isRequired,
        color: PropTypes.object.isRequired,
        imageIndex: PropTypes.number.isRequired,
        selected: PropTypes.bool.isRequired,
        navType: PropTypes.string.isRequired,
        allowMove: PropTypes.bool.isRequired,
        onSelect: PropTypes.func.isRequired,
        onMove: PropTypes.func.isRequired,
    }
}

function preventDraggingOutOfStage(img, pos) {
    const stageSize = img.getStage().size()
    const size = img.size()
    const scale = img.scale()

    return {
        x: _.clamp(pos.x, 0, stageSize.width - size.width * scale.x),
        y: _.clamp(pos.y, 0, stageSize.height - size.height * scale.y),
    }
}

export default PartImage
